/**
 * Prompts Library — biblioteca de prompts reutilizables (Rocket Landing + futuras).
 */
import { h, apiClient, toast, emptyState, skeletonCard } from './_common.js';

let _selectedColId = null;

export async function renderPrompts(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'max-w-6xl mx-auto px-6 py-6 space-y-4' });
  root.appendChild(wrap);

  wrap.appendChild(h('div', {}, [
    h('h2', { class: 'font-display text-2xl font-bold' }, '📚 Biblioteca de Prompts'),
    h('p', { class: 'text-sm text-ink-300' }, 'Prompts probados copy-paste para Nano Banana, VEO 3, Claude Design y Claude Code. Click en cualquiera para copiar al portapapeles.'),
  ]));

  // Sidebar (colecciones) + contenido (prompts de colección seleccionada)
  const layout = h('div', { class: 'grid grid-cols-1 md:grid-cols-4 gap-4' });
  const sidebar = h('aside', { class: 'space-y-2' });
  const content = h('div', { class: 'md:col-span-3 space-y-3' });
  layout.appendChild(sidebar);
  layout.appendChild(content);
  wrap.appendChild(layout);

  // Skeleton
  for (let i = 0; i < 2; i++) sidebar.appendChild(skeletonCard(2));
  content.appendChild(skeletonCard(3));

  try {
    const colsRes = await apiClient.get('/api/v1/prompts/collections');
    sidebar.innerHTML = '';
    if (!colsRes.collections || colsRes.collections.length === 0) {
      sidebar.appendChild(emptyState('📚', 'Sin colecciones todavía', ''));
      content.innerHTML = '';
      return;
    }
    if (!_selectedColId) _selectedColId = colsRes.collections[0].id;
    colsRes.collections.forEach((c) => {
      const isActive = c.id === _selectedColId;
      sidebar.appendChild(h('button', {
        class: `w-full text-left p-3 rounded-lg border transition ${isActive ? 'bg-gold-500/10 border-gold-400/40' : 'bg-base-800/50 border-base-700 hover:border-gold-400/40'}`,
        onclick: async () => { _selectedColId = c.id; await renderPrompts(root); },
      }, [
        h('div', { class: 'flex items-center gap-2' }, [
          h('span', { class: 'text-xl' }, c.emoji),
          h('span', { class: `font-semibold text-sm ${isActive ? 'text-gold-300' : 'text-ink-100'}` }, c.name),
        ]),
        h('div', { class: 'text-xs text-ink-400 mt-1' }, c.description),
        h('div', { class: 'text-xs text-ink-500 mt-1' }, `${c.promptCount} prompts${c.source ? ' · ' + c.source : ''}`),
      ]));
    });

    // Cargar colección seleccionada
    content.innerHTML = '';
    const colRes = await apiClient.get(`/api/v1/prompts/collections/${_selectedColId}`);
    content.appendChild(h('div', { class: 'mb-4' }, [
      h('div', { class: 'flex items-center gap-2 mb-1' }, [
        h('span', { class: 'text-2xl' }, colRes.emoji),
        h('h3', { class: 'font-display text-xl font-bold' }, colRes.name),
      ]),
      h('p', { class: 'text-sm text-ink-300' }, colRes.description),
      colRes.source ? h('p', { class: 'text-xs text-ink-500 mt-1' }, `Fuente: ${colRes.source}`) : null,
    ]));

    (colRes.prompts || []).forEach((p) => content.appendChild(promptCard(p)));
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(h('div', { class: 'text-red-400 text-sm' }, 'Error cargando prompts: ' + (e?.message || e)));
  }
}

function promptCard(p) {
  const card = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 hover:border-gold-400/40 transition' });
  card.appendChild(h('div', { class: 'flex items-start justify-between gap-3 mb-2' }, [
    h('div', { class: 'flex items-center gap-2' }, [
      p.emoji ? h('span', { class: 'text-xl' }, p.emoji) : null,
      h('div', {}, [
        h('div', { class: 'font-semibold text-sm' }, [
          p.title,
          p.variant ? h('span', { class: 'text-xs text-gold-300 ml-2' }, `(Variante ${p.variant})`) : null,
        ]),
        h('div', { class: 'text-xs text-ink-400' }, `${p.tool}${p.category ? ' · ' + p.category : ''}`),
      ]),
    ]),
    h('button', {
      class: 'shrink-0 bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 text-xs px-3 py-1.5 rounded-md transition',
      onclick: async () => {
        try {
          await navigator.clipboard.writeText(p.prompt);
          toast('📋 Prompt copiado al portapapeles', 'success');
        } catch {
          toast('No se pudo copiar (revisa permisos navegador)', 'error');
        }
      },
    }, '📋 Copiar'),
  ]));
  if (p.description) card.appendChild(h('p', { class: 'text-xs text-ink-300 mb-2' }, p.description));
  if (p.input) card.appendChild(h('p', { class: 'text-xs text-amber-300 mb-2' }, `📎 Input requerido: ${p.input}`));
  const preview = h('pre', {
    class: 'text-xs bg-base-900/70 border border-base-700 rounded-lg p-3 max-h-32 overflow-hidden whitespace-pre-wrap text-ink-300 cursor-pointer',
    style: 'font-family: ui-monospace, monospace;',
    onclick: () => {
      preview.style.maxHeight = preview.style.maxHeight === 'none' ? '128px' : 'none';
    },
  }, p.prompt.slice(0, 600) + (p.prompt.length > 600 ? '\n\n[Click para ver completo o pulsa Copiar]' : ''));
  card.appendChild(preview);
  return card;
}
